import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Trans, t } from "@lingui/macro";

import { i18n } from "base/i18n";
import { useUserStore } from "base/stores";
import { Modal } from "widgets/modal";
import { Button, BUTTON_TYPES, MultiLine } from "widgets/inputs";
import { Icon } from "widgets/datadisplay";
import { ErrorBoundary } from "widgets/layouts";
import { FieldErrors } from "widgets/others";
import { Validation, Validator, ValidateHelper } from "widgets/validation";
import { SingleUserSelect } from "widgets/singleuserselect";

import styles from "./reassign.css";

import ExclamatoryIcon from "./ExclamatoryIcon.svg";
export function Reassign({
  onBulkReassign,
  closeReassignModal,
  parentComponent
}) {
  let [comments, setComments] = useState(null);
  let [selectedUser, setSelectedUser] = useState(null);
  let [error, setError] = useState({});
  let [userList, setUserList] = useState([]);

  const validationRef = useRef(null);
  const { currentUser } = useUserStore.getState();

  useEffect(function getUsersList() {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getUsers(query = "") {
    let users = await useUserStore.getState().getUsers(query);
    if (parentComponent !== "admin") {
      users = users?.filter((user) => user._id !== currentUser._id);
    }
    setUserList(users);
  }

  function handleReassign() {
    // setDisabled(true);
    let err = validationRef.current && validationRef.current.validate();
    if (
      (!comments?.trim()?.length || !selectedUser) &&
      err &&
      (err.comments || err.reassignTo)
    ) {
      onValidate(err);
    } else {
      let reassignDetails = {
        ReassignTo: selectedUser,
        Note: comments
      };
      onBulkReassign(reassignDetails);
      closeReassignModal();
    }
  }

  function onValidate(newErr) {
    let newError = { ...error, ...newErr };
    setError(newError);
  }

  function setAssignedTo(assignee) {
    setSelectedUser(assignee);
  }

  return (
    <Modal
      titleRenderer={() => <Trans>Reassign</Trans>}
      rightActionsRenderer={() => (
        <div className={styles.buttonActionStyle}>
          <Button type={BUTTON_TYPES.SECONDARY} onClick={closeReassignModal}>
            <Trans>Cancel</Trans>
          </Button>
          <Button type={BUTTON_TYPES.PRIMARY} onClick={() => handleReassign()}>
            <Trans>Reassign</Trans>
          </Button>
        </div>
      )}
      open={true}
      onClose={closeReassignModal}
      bodyStyle={{
        backgroundColor: "var(--white)",
        padding:
          "var(--space-16) var(--space-20) var(--space-20) var(--space-20)"
      }}
      className={styles.formModalStyle}
    >
      <ErrorBoundary>
        {parentComponent === "admin" && (
          <div className={styles.reassignNotice}>
            <Icon paddingNone={true}>
              <ExclamatoryIcon />
            </Icon>
            <span className={`smallplus`}>
              <Trans>
                The items associated with the parallel branch will be skipped
                while reassigning
              </Trans>
            </span>
          </div>
        )}
        <div className={styles.reassignContent}>
          <Validation ref={validationRef}>
            <div className={styles.reassignSection}>
              <div className={`small`}>
                <Trans>Reassign to</Trans>
                <span className={styles.requiredLabel}>*</span>
              </div>
              <Validator
                name="reassignTo"
                value={selectedUser}
                onValidate={onValidate}
                validations={[ValidateHelper.required]}
              >
                <SingleUserSelect
                  value={selectedUser}
                  onChange={(assignee) => setAssignedTo(assignee)}
                  dataSource={userList}
                  className={`${
                    error && error.reassignTo ? styles.errorBox : null
                  }`}
                  onSearchDebounced={(evt) => getUsers(evt.search)}
                  labelRenderer={(user) => <div>{user.Name}</div>}
                />
              </Validator>
              {error && error.reassignTo && (
                <FieldErrors
                  errors={[
                    <Trans key="reassignToError">Reassign to is required</Trans>
                  ]}
                />
              )}
            </div>
            <div className={styles.reassignSection}>
              <Validator
                name="comments"
                value={comments}
                onValidate={onValidate}
                validations={[ValidateHelper.required]}
              >
                <MultiLine
                  label={i18n._(t`Notes`)}
                  required={true}
                  autoGrow={false}
                  value={comments}
                  name="comments"
                  onChange={(e) => setComments(e.target.value)}
                  showCharacterLimitHint={true}
                />
              </Validator>
              {error && error.comments && (
                <FieldErrors
                  errors={[<Trans key="noteError">Note is required</Trans>]}
                />
              )}
            </div>
          </Validation>
        </div>
      </ErrorBoundary>
    </Modal>
  );
}
Reassign.propTypes = {
  closeReassignModal: PropTypes.func,
  onBulkReassign: PropTypes.func,
  parentComponent: PropTypes.bool
};
